import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import SectionLabel from '@/components/SectionLabel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NominatePage = () => {
  const [formData, setFormData] = useState({
    nominatorName: '',
    nominatorEmail: '',
    nomineeName: '',
    nomineeTitle: '',
    nomineeCompany: '',
    nomineeCountry: '',
    whatBuilt: '',
    linkedinUrl: '',
    whyBelongs: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/nominations`, {
        name: formData.nomineeName,
        company: formData.nomineeCompany,
        linkedin_url: formData.linkedinUrl,
        reason: `What she built: ${formData.whatBuilt}\n\nWhy she belongs: ${formData.whyBelongs}\n\nTitle: ${formData.nomineeTitle}\nCountry: ${formData.nomineeCountry}`,
        nominator_email: formData.nominatorEmail,
      });

      setSubmitted(true);
      toast.success('Nomination submitted successfully!');
    } catch (error) {
      console.error('Nomination error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="nominate-page" className="min-h-screen bg-[#FAF9F6] pt-32">
        <div className="max-w-[600px] mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 bg-[#C25E3E] rounded-full flex items-center justify-center mx-auto mb-8">
            <svg className="w-10 h-10 text-[#080808]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A1A1A' }}>
            Thank You
          </h1>
          <p className="text-lg mb-8" style={{ fontFamily: "'Lora', serif", color: '#3A3A3A' }}>
            Dr. Neha Dewan reviews every nomination personally. 
            You will hear from us within 14 days.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                nominatorName: '',
                nominatorEmail: '',
                nomineeName: '',
                nomineeTitle: '',
                nomineeCompany: '',
                nomineeCountry: '',
                whatBuilt: '',
                linkedinUrl: '',
                whyBelongs: '',
              });
            }}
            className="inline-flex items-center h-12 px-8 border border-[#C25E3E] text-[#C25E3E] text-[12px] tracking-[0.1em] uppercase transition-all hover:bg-[#C25E3E] hover:text-[#080808]"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            Nominate Another Woman
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="nominate-page" className="min-h-screen bg-[#FAF9F6]">
      <SEO 
        title="Nominate a Woman Leader — She Built This"
        description="Know a woman who built something extraordinary? Nominate her for She Built This. Every nomination is reviewed personally by Dr. Neha Dewan."
        keywords={['nominate women leaders', 'women leadership recognition', 'She Built This nomination', 'celebrate women founders']}
        url="https://shebuiltthis.com/nominate"
        image="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=1200&q=80"
      />
      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-[800px] mx-auto text-center">
          <p className="text-[10px] tracking-[0.35em] uppercase mb-6" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>NOMINATE</p>
          <h1 className="text-4xl lg:text-[64px] leading-[1.0] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: '#1A1A1A' }}>
            Know a woman who belongs here?
          </h1>
          <p className="text-lg" style={{ fontFamily: "'Lora', serif", color: '#3A3A3A' }}>
            Every nomination is reviewed personally by Dr. Neha Dewan. 
            Every profile is verified before publication.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding">
        <div className="max-w-[700px] mx-auto px-6">
          <form onSubmit={handleSubmit} className="bg-[#1A1A1A] border border-[rgba(201,168,76,0.12)] p-8 lg:p-12">
            {/* Section: About You */}
            <div className="mb-10">
              <h2 className="font-mono text-xs text-[#C25E3E] tracking-wider uppercase mb-6">
                About You
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs tracking-wide uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="nominatorName"
                    value={formData.nominatorName}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="input-nominator-name"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-wide uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                    Your Email *
                  </label>
                  <input
                    type="email"
                    name="nominatorEmail"
                    value={formData.nominatorEmail}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="input-nominator-email"
                  />
                </div>
              </div>
            </div>

            {/* Section: About the Nominee */}
            <div className="mb-10">
              <h2 className="font-mono text-xs text-[#C25E3E] tracking-wider uppercase mb-6">
                About the Woman You're Nominating
              </h2>
              <div className="space-y-5">
                <div>
                  <label className="text-xs tracking-wide uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                    Her Name *
                  </label>
                  <input
                    type="text"
                    name="nomineeName"
                    value={formData.nomineeName}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="input-nominee-name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs tracking-wide uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                      Her Title / Role *
                    </label>
                    <input
                      type="text"
                      name="nomineeTitle"
                      value={formData.nomineeTitle}
                      onChange={handleInputChange}
                      required
                      className="input-field"
                      data-testid="input-nominee-title"
                    />
                  </div>
                  <div>
                    <label className="text-xs tracking-wide uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                      Her Organisation *
                    </label>
                    <input
                      type="text"
                      name="nomineeCompany"
                      value={formData.nomineeCompany}
                      onChange={handleInputChange}
                      required
                      className="input-field"
                      data-testid="input-nominee-company"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs tracking-wide uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                    Her Country *
                  </label>
                  <input
                    type="text"
                    name="nomineeCountry"
                    value={formData.nomineeCountry}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="input-nominee-country"
                  />
                </div>

                <div>
                  <label className="text-xs tracking-wide uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                    What did she build? * <span className="text-sbt-dim">(200 words max)</span>
                  </label>
                  <textarea
                    name="whatBuilt"
                    value={formData.whatBuilt}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    maxLength={1200}
                    placeholder="Describe what she built — a company, institution, policy, program, or other significant achievement..."
                    className="input-field resize-none"
                    data-testid="input-what-built"
                  />
                </div>

                <div>
                  <label className="text-xs tracking-wide uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                    Link to her LinkedIn or Website
                  </label>
                  <input
                    type="url"
                    name="linkedinUrl"
                    value={formData.linkedinUrl}
                    onChange={handleInputChange}
                    placeholder="https://..."
                    className="input-field"
                    data-testid="input-linkedin"
                  />
                </div>

                <div>
                  <label className="text-xs tracking-wide uppercase block mb-2" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>
                    Why does her story belong on She Built This? * <span className="text-sbt-dim">(150 words max)</span>
                  </label>
                  <textarea
                    name="whyBelongs"
                    value={formData.whyBelongs}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={900}
                    placeholder="Explain why her achievements deserve to be documented..."
                    className="input-field resize-none"
                    data-testid="input-why-belongs"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="submit-nomination"
            >
              {loading ? 'Submitting...' : 'Submit Nomination'}
            </button>

            <p className="font-mono text-xs text-sbt-dim text-center mt-6">
              All nominations are reviewed within 14 days. Profiles are only published 
              after verification of all facts and sources.
            </p>
          </form>
        </div>
      </section>

      {/* Process Info */}
      <section className="bg-sbt-black section-padding">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-12">
            <SectionLabel>The Process</SectionLabel>
            <h2 className="font-display text-4xl font-bold text-sbt-white mt-4">
              How nominations become profiles
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'You Nominate', desc: 'Submit the form with details about her achievements.' },
              { step: '02', title: 'Review', desc: 'Dr. Neha Dewan personally reviews every nomination.' },
              { step: '03', title: 'Verification', desc: 'We verify all facts against official sources.' },
              { step: '04', title: 'Publication', desc: 'Verified profiles are published permanently.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <span className="text-5xl font-bold text-[#C25E3E]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {item.step}
                </span>
                <h3 className="font-subheading text-lg font-bold text-sbt-white mt-4 mb-2">
                  {item.title}
                </h3>
                <p className="font-body text-sm text-sbt-dim">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default NominatePage;
